/**
 * Save queue — at most one autosave request in flight per editor.
 *
 * A segment save carries the version it expects to replace. A second save sent
 * while the first is still out carries that same version (the first has not
 * answered with the new one yet), so the server rejects it as a version
 * conflict and the writer is shown a conflict dialog about their own typing.
 *
 * So a save requested while another is in flight is not sent alongside it.
 * Every request made during one flight shares a single follow-up, started the
 * moment that save succeeds. The follow-up uses the save function passed by the
 * latest request, and reads whatever is pending when it starts — so only the
 * latest text goes out, against the version the settled save returned.
 *
 * A save that does not succeed (it resolves false — failed, conflicted,
 * blocked — or throws) drops the follow-up and answers false to everyone
 * queued behind it. The editor has already moved to its retry, conflict or
 * blocked state, and that state owns the next attempt: an immediate second
 * request would jump the retry backoff or write over a conflict.
 */

export type SaveRun = () => Promise<boolean>

export interface SaveQueue {
  /**
   * Run `save` now, or — while a save is in flight — once after it succeeds.
   * Resolves with the result of the run that covers this request.
   */
  run(save: SaveRun): Promise<boolean>
  /** Whether a save is in flight right now. */
  isSaving(): boolean
  /**
   * Drop the queued follow-up, if any, answering false to whoever is waiting
   * on it. The in-flight save is left to finish, and the queue keeps working.
   */
  cancelPending(): void
}

interface FollowUp {
  save: SaveRun
  promise: Promise<boolean>
  resolve: (result: boolean | Promise<boolean>) => void
}

export function createSaveQueue(): SaveQueue {
  let inFlight = false
  let followUp: FollowUp | null = null

  function start(save: SaveRun): Promise<boolean> {
    inFlight = true
    let result: Promise<boolean>
    try {
      result = save()
    } catch (error) {
      result = Promise.reject(error instanceof Error ? error : new Error(String(error)))
    }
    result.then(
      (succeeded) => { handOver(succeeded) },
      () => { handOver(false) },
    )
    return result
  }

  // Runs as the in-flight save settles, and starts the follow-up in the same
  // step — there is no gap in which another request could start a second save.
  function handOver(succeeded: boolean) {
    inFlight = false
    const next = followUp
    followUp = null
    if (!next) return
    next.resolve(succeeded ? start(next.save) : false)
  }

  return {
    run(save) {
      if (!inFlight) return start(save)
      if (followUp) {
        followUp.save = save
        return followUp.promise
      }
      let resolve: FollowUp['resolve'] = () => {}
      const promise = new Promise<boolean>((res) => { resolve = res })
      followUp = { save, promise, resolve }
      return promise
    },
    isSaving: () => inFlight,
    cancelPending() {
      const dropped = followUp
      followUp = null
      dropped?.resolve(false)
    },
  }
}
